import { useKhalAuth } from '@khal-os/sdk/app';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	Tooltip,
} from '@khal-os/ui';
import { Building2, LogOut, Shield, User } from 'lucide-react';
import { useCallback } from 'react';
import {
	buildPlatformLogoutUrl,
	clearAllKhalStorage,
	getActiveKernelToken,
} from '../../lib/platform-logout';

const isTauri = typeof window !== 'undefined' && !!(window as any).__TAURI__;

const DEFAULT_PLATFORM_URL = 'https://platform.khal.ai';

function resolvePlatformUrl(): string {
	// `import.meta.env` is populated by Vite in host builds; guard for tests
	// and non-Vite consumers of the desktop-shell package.
	const env =
		typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env;
	const configured = env ? env.VITE_PLATFORM_API_URL : undefined;
	return typeof configured === 'string' && configured.length > 0 ? configured : DEFAULT_PLATFORM_URL;
}

const ROLE_COLORS: Record<string, string> = {
	'platform-owner': 'var(--khal-status-warning)',
	'platform-admin': 'var(--khal-accent-primary)',
	'platform-dev': 'var(--khal-accent-secondary)',
	member: 'var(--khal-text-muted)',
};

const ROLE_LABELS: Record<string, string> = {
	'platform-owner': 'Owner',
	'platform-admin': 'Admin',
	'platform-dev': 'Developer',
	member: 'Member',
};

/**
 * UserMenu component.
 *
 * NOTE: The original UserMenu depends on WorkOS `useAuth()` and a server-side
 * logout form. In the desktop-shell package, we use `useKhalAuth()` from the SDK
 * which works with any auth provider. The logout mechanism is abstracted.
 */
export function UserMenu() {
	const khalAuth = useKhalAuth();

	const handleLogout = useCallback(() => {
		if (isTauri) {
			(window as any).__TAURI__.core.invoke('switch_account').catch(console.error);
			return;
		}
		// PWA: read the current kernel-session JWT BEFORE wiping storage so
		// platform can pull the `sid` claim and revoke the WorkOS session.
		// Then wipe every khal-* entry (windows/tabs/keybinds/enterprise
		// config) and bounce through platform's /v1/auth/logout — the route
		// validates the returnTo origin, calls workos.getLogoutUrl, and
		// 302s the browser through WorkOS's hosted /sessions/logout back
		// here unauthenticated. The main.tsx auth gate will then render the
		// kernel picker on the next load.
		const token = getActiveKernelToken();
		clearAllKhalStorage();
		const logoutUrl = buildPlatformLogoutUrl({
			platformUrl: resolvePlatformUrl(),
			pwaOrigin: window.location.origin,
			token,
		});
		window.location.assign(logoutUrl);
	}, []);

	if (khalAuth?.loading || !khalAuth?.userId) {
		return (
			<div
				className="flex h-7 w-7 items-center justify-center rounded-full"
				style={{ color: 'var(--khal-text-muted)' }}
			>
				<User size={14} />
			</div>
		);
	}

	// Prefer WorkOS-supplied name/email; raw userId (user_01…) is a last-resort
	// fallback for legacy tokens that haven't gone through the enriched
	// platform callback yet.
	const displayName = khalAuth.name ?? khalAuth.email ?? khalAuth.userId;
	const initial = (khalAuth.name ?? khalAuth.email ?? khalAuth.userId).charAt(0).toUpperCase();
	const picture = khalAuth.picture;
	const role = khalAuth.role ?? 'member';
	const roleColor = ROLE_COLORS[role] ?? ROLE_COLORS.member;

	return (
		<>
			<DropdownMenu>
				<Tooltip text={displayName} position="top" delay delayTime={400} desktopOnly>
					<DropdownMenuTrigger
						className="flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:scale-105 active:scale-95"
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLElement).style.background = 'var(--khal-taskbar-hover-bg)';
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLElement).style.background = '';
						}}
						aria-label="User menu"
					>
						{picture ? (
							<img
								src={picture}
								alt={displayName}
								className="h-5 w-5 rounded-full object-cover"
								onError={(e) => {
									// If the WorkOS-provided avatar fails to load (e.g. Google
									// CDN hiccup), swap in the initial-letter fallback so the
									// button is never empty.
									(e.currentTarget as HTMLImageElement).style.display = 'none';
									const fallback = (e.currentTarget.nextElementSibling as HTMLElement | null);
									if (fallback) fallback.style.display = 'flex';
								}}
							/>
						) : null}
						<span
							className="flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold"
							style={{
								display: picture ? 'none' : 'flex',
								background: 'var(--khal-accent-primary)',
								color: 'var(--khal-text-inverse)',
							}}
						>
							{initial}
						</span>
					</DropdownMenuTrigger>
				</Tooltip>
				<DropdownMenuContent align="end" side="top" className="w-56">
					<DropdownMenuLabel className="flex flex-col gap-0.5 font-normal">
						<span className="text-copy-13 font-medium" style={{ color: 'var(--khal-text-primary)' }}>
							{displayName}
						</span>
						{khalAuth.email && khalAuth.name && khalAuth.email !== khalAuth.name ? (
							<span className="text-label-12" style={{ color: 'var(--khal-text-muted)' }}>
								{khalAuth.email}
							</span>
						) : null}
					</DropdownMenuLabel>

					<DropdownMenuSeparator />

					{khalAuth.orgId && (
						<DropdownMenuItem disabled className="gap-2 opacity-100">
							<Building2 size={14} style={{ color: 'var(--khal-text-muted)' }} />
							<span className="truncate" style={{ color: 'var(--khal-text-secondary)' }}>
								{khalAuth.orgId}
							</span>
						</DropdownMenuItem>
					)}

					<DropdownMenuItem disabled className="gap-2 opacity-100">
						<Shield size={14} style={{ color: roleColor }} />
						<span
							className="rounded px-1.5 py-0.5 text-label-12 font-medium"
							style={{
								background: `color-mix(in srgb, ${roleColor} 15%, transparent)`,
								color: roleColor,
							}}
						>
							{ROLE_LABELS[role] ?? role}
						</span>
					</DropdownMenuItem>

					<DropdownMenuSeparator />

					<DropdownMenuItem className="gap-2" onClick={handleLogout}>
						<LogOut size={14} />
						Logout
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</>
	);
}
